import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, X, Save, Loader2, Trash2} from 'lucide-react';
import { getRequest, postRequest } from "../../services/apiService";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DeleteEquipmentActivityMappingDialog from './DeleteEquipmentActivityMappingDialog';

const CompartmentActivityMapping = ({ compartmentId }) => {
  const [activities, setActivities] = useState([]);
  const [compartmentActivities, setCompartmentActivities] = useState([]);
  const [originalCompartmentActivities, setOriginalCompartmentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [savingChanges, setSavingChanges] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isActivitiesDropdownOpen, setIsActivitiesDropdownOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState('');
  const [addActivityMode, setAddActivityMode] = useState(false);
  const [newActivityName, setNewActivityName] = useState('');
  const [newActivityDescription, setNewActivityDescription] = useState('');
  const [addActivityLoading, setAddActivityLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const activitiesDropdownRef = useRef(null);
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [selectedActivityToDelete, setSelectedActivityToDelete] = useState(null);
  const [deleteActivityLoading, setDeleteActivityLoading] = useState(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getDate()} ${date.toLocaleString('default', { month: 'short' })}, ${date.getFullYear()}`;
  };

  const generateBoxColor = (index) => {
    const colors = [
      'bg-blue-100 border-blue-300',
      'bg-purple-100 border-purple-300',
      'bg-green-100 border-green-300',
      'bg-yellow-100 border-yellow-300',
      'bg-pink-100 border-pink-300',
      'bg-indigo-100 border-indigo-300',
      'bg-red-100 border-red-300',
      'bg-teal-100 border-teal-300',
      'bg-orange-100 border-orange-300',
      'bg-cyan-100 border-cyan-300'
    ];
    return colors[index % colors.length];
  };

  const generateDotColor = (index) => {
    const colors = [
      'bg-blue-500',
      'bg-purple-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-teal-500',
      'bg-orange-500',
      'bg-cyan-500'
    ];
    return colors[index % colors.length];
  };

  const getActivityIcon = (activityName) => {
    const baseClasses = "h-5 w-5 text-gray-700";
    const activityNameLower = activityName.toLowerCase();
    
    if (activityNameLower.includes('clean') || activityNameLower.includes('sanitize')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      );
    }
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
    );
  };

  const showToast = (severity, detail) => {
    if (severity === 'success') {
      toast.success(detail, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else {
      toast.error(detail, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  useEffect(() => {
    const fetchCompartmentActivities = async () => {
      setLoading(true);
      try {
        const response = await getRequest(`master/compartment-activity-mapping/?compartment_id=${compartmentId}`);
        const sortedActivities = response.results.data.sort((a, b) => a.sequence - b.sequence);
        setCompartmentActivities(sortedActivities);
        setOriginalCompartmentActivities(JSON.parse(JSON.stringify(sortedActivities)));
      } catch (err) {
        console.error("Error fetching compartment activities:", err);
        setError("Failed to load compartment activities");
      } finally {
        setLoading(false);
      }
    };

    if (compartmentId) fetchCompartmentActivities();
    
    // Call fetchActivities when component mounts
    fetchActivities();
  }, [compartmentId]);

  const fetchActivities = async () => {
    setActivitiesLoading(true);
    try {
      // Updated to use the correct endpoint with dropdown parameter
      const response = await getRequest('master/compartment-activity/?dropdown=true');
      
      // Update this to match the actual structure of your API response
      // Based on the paste-2.txt response structure
      setActivities(response.results || response);
      
      console.log("Fetched activities:", response);
    } catch (error) {
      console.error("Error fetching activities:", error);
      showToast('error', 'Failed to fetch activities');
    } finally {
      setActivitiesLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activitiesDropdownRef.current && !activitiesDropdownRef.current.contains(event.target)) {
        setIsActivitiesDropdownOpen(false);
        if (addActivityMode) {
          setAddActivityMode(false);
          setNewActivityName('');
          setNewActivityDescription('');
          setErrorMessage('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [addActivityMode]);

  const filteredActivities = activities.filter(activity => 
    activity.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const searchTermNotFound = searchTerm.trim() !== '' && 
    filteredActivities.length === 0 && 
    !activities.some(activity => activity.name.toLowerCase() === searchTerm.toLowerCase());

  const handleActivitySelect = (activity) => {
    setSelectedActivity(activity.id);
    setIsActivitiesDropdownOpen(false);
  };

  const toggleAddActivityMode = () => {
    setAddActivityMode(true);
    setNewActivityName(searchTerm);
    setErrorMessage('');
  };

  const handleAddNewActivity = async () => {
    if (!newActivityName.trim()) {
      setErrorMessage('Activity name is required');
      return;
    }

    setAddActivityLoading(true);
    try {
      await postRequest("master/compartment-activity/", {
        name: newActivityName,
        description: newActivityDescription,
        status_db: 1
      });

      await fetchActivities();
      setSelectedActivity('');
      setAddActivityMode(false);
      showToast('success', 'Activity added successfully');
    } catch (error) {
      console.error("Error adding activity:", error);
      setErrorMessage('Failed to add activity. Please try again.');
    } finally {
      setAddActivityLoading(false);
    }
  };

  const handleCancelAddActivity = () => {
    setAddActivityMode(false);
    setNewActivityName('');
    setNewActivityDescription('');
    setErrorMessage('');
  };

  const handleDragStart = (index) => setDraggedItemIndex(index);

  const handleDrop = (dropIndex) => {
    if (draggedItemIndex === null) return;
    
    const newActivities = [...compartmentActivities];
    const [draggedItem] = newActivities.splice(draggedItemIndex, 1);
    newActivities.splice(dropIndex, 0, draggedItem);
    
    const updatedActivities = newActivities.map((activity, idx) => ({
      ...activity,
      sequence: idx + 1
    }));
    
    setCompartmentActivities(updatedActivities);
    setHasChanges(true);
  };

  const handleSaveSequenceChanges = async () => {
    setSavingChanges(true);
    try {
      const payload = compartmentActivities.map(activity => ({
        id: activity.id || null,
        compartment_activity: activity.compartment_activity.id,
        sequence: activity.sequence
      }));

      await postRequest(
        `master/compartment-activity-mapping/${compartmentId}/`,
        payload
      );

      const response = await getRequest(
        `master/compartment-activity-mapping/?compartment_id=${compartmentId}`
      );
      const sorted = response.results.data.sort((a, b) => a.sequence - b.sequence);
      setCompartmentActivities(sorted);
      setOriginalCompartmentActivities(JSON.parse(JSON.stringify(sorted)));
      setHasChanges(false);
      showToast('success', 'Activities sequence updated successfully');
    } catch (error) {
      console.error("Error updating activity sequences:", error);
      showToast('error', 'Failed to update activity sequences');
    } finally {
      setSavingChanges(false);
    }
  };

  const handleAddCompartmentActivity = async () => {
    if (!selectedActivity) {
      showToast('error', 'Please select an activity');
      return;
    }
    
    setSavingChanges(true);
    try {
      const newSequence = compartmentActivities.length > 0 
        ? Math.max(...compartmentActivities.map(item => item.sequence)) + 1
        : 1;

      const payload = [
        ...compartmentActivities.map(activity => ({
          id: activity.id,
          compartment_activity: activity.compartment_activity.id,
          sequence: activity.sequence
        })),
        {
          id: null,
          compartment_activity: selectedActivity,
          sequence: newSequence
        }
      ];

      await postRequest(
        `master/compartment-activity-mapping/${compartmentId}/`,
        payload
      );

      const response = await getRequest(
        `master/compartment-activity-mapping/?compartment_id=${compartmentId}`
      );
      const sorted = response.results.data.sort((a, b) => a.sequence - b.sequence);
      setCompartmentActivities(sorted);
      setOriginalCompartmentActivities(JSON.parse(JSON.stringify(sorted)));
      setSelectedActivity('');
      showToast('success', 'Activity added successfully');
    } catch (error) {
      console.error("Error adding compartment activity:", error);
      showToast('error', 'Failed to add activity');
    } finally {
      setSavingChanges(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
        <span className="ml-2 text-gray-600">Loading activities...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
        {error}
      </div>
    );
  }

  const handleDeleteActivity = async () => {
    if (!selectedActivityToDelete) return;

    setDeleteActivityLoading(selectedActivityToDelete);
    
    try {
      // Find the activity to delete before filtering
      const activityToDelete = compartmentActivities.find(
        a => a.id === selectedActivityToDelete
      );
      
      if (!activityToDelete) {
        showToast('error', 'Activity not found');
        return;
      }

      // Prepare payload with status_db:3 for deletion
      const payload = [
        ...compartmentActivities
          .filter(activity => activity.id !== selectedActivityToDelete)
          .map((activity, index) => ({
            id: activity.id,
            compartment_activity: activity.compartment_activity.id,
            sequence: index + 1
          })),
        {
          id: activityToDelete.id,
          compartment_activity: activityToDelete.compartment_activity.id,
          sequence: activityToDelete.sequence,
          status_db: 3  // Mark as deleted
        }
      ];

      // API call to update sequences and mark as deleted
      await postRequest(
        `master/compartment-activity-mapping/${compartmentId}/`, 
        payload
      );
      
      // Update state with new activities
      const updatedActivities = compartmentActivities
        .filter(activity => activity.id !== selectedActivityToDelete)
        .map((activity, index) => ({
          ...activity,
          sequence: index + 1
        }));
      
      setCompartmentActivities(updatedActivities);
      setOriginalCompartmentActivities(JSON.parse(JSON.stringify(updatedActivities)));
      
      // Show success message
      showToast('success', 'Activity removed successfully');
    } catch (error) {
      console.error("Error deleting activity:", error);
      showToast('error', 'Failed to remove activity');
    } finally {
      setDeleteActivityLoading(null);
      setDeleteDialogVisible(false);
      setSelectedActivityToDelete(null);
    }
  };

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <ToastContainer />
      <DeleteEquipmentActivityMappingDialog
        visible={deleteDialogVisible}
        onHide={() => {
          setDeleteDialogVisible(false);
          setSelectedActivityToDelete(null);
        }}
        activity={compartmentActivities.find(a => a.id === selectedActivityToDelete)?.compartment_activity}
        onDelete={handleDeleteActivity}
      />

      
      <div className="max-w-6xl mx-auto">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Compartment Activity Mapping</h2>
        
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="relative w-full md:w-1/2" ref={activitiesDropdownRef}>
            <div className="text-gray-700 mb-2 font-medium">Select Activity</div>
            <div 
              className="border rounded-lg bg-white p-2 flex justify-between items-center cursor-pointer shadow-sm"
              onClick={() => !addActivityMode && setIsActivitiesDropdownOpen(!isActivitiesDropdownOpen)}
            >
              <div className="flex-1 truncate">
                {activities.find(a => a.id === selectedActivity)?.name || "Select an activity..."}
              </div>
              <div className="relative">
                {activitiesLoading ? (
                  <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                ) : (
                  <Search className="h-5 w-5 text-gray-400" />
                )}
                <input
                  type="text"
                  className="absolute inset-0 w-full opacity-0 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsActivitiesDropdownOpen(true);
                  }}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSearchTerm(e.target.value);
                    if (addActivityMode) {
                      setNewActivityName(e.target.value);
                    }
                  }}
                  value={addActivityMode ? newActivityName : searchTerm}
                />
              </div>
            </div>
            
            {isActivitiesDropdownOpen && !addActivityMode && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <div className="p-2 border-b">
                  <input
                    type="text"
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Search activities..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div>
                  {activitiesLoading ? (
                    <div className="p-4 text-center">
                      <Loader2 className="h-6 w-6 text-blue-500 animate-spin mx-auto" />
                      <p className="mt-2 text-gray-500">Loading activities...</p>
                    </div>
                  ) : (
                    <>
                      {filteredActivities.length > 0 ? (
                        filteredActivities.map((activity) => (
                          <div
                            key={activity.id}
                            className="p-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleActivitySelect(activity)}
                          >
                            {activity.name}
                          </div>
                        ))
                      ) : (
                        <div className="p-2 text-gray-500 text-center">No activities found</div>
                      )}
                      
                      {searchTermNotFound && (
                        <div 
                          className="p-2 border-t flex items-center justify-center bg-blue-50 hover:bg-blue-100 cursor-pointer text-blue-600"
                          onClick={toggleAddActivityMode}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          <span>Add "{searchTerm}" as new activity</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
            
            {isActivitiesDropdownOpen && addActivityMode && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg overflow-visible">
                <div className="p-3 border-b">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-medium text-gray-800">Add New Activity</h3>
                    <button 
                      className="text-gray-500 hover:text-gray-700"
                      onClick={handleCancelAddActivity}
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  
                  {errorMessage && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                      {errorMessage}
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Activity Name
                    </label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={newActivityName}
                      onChange={(e) => setNewActivityName(e.target.value)}
                      placeholder="Enter activity name"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description (Optional)
                    </label>
                    <textarea
                      className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={newActivityDescription}
                      onChange={(e) => setNewActivityDescription(e.target.value)}
                      placeholder="Enter activity description"
                      rows={2}
                    ></textarea>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="mr-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      onClick={handleCancelAddActivity}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
                      onClick={handleAddNewActivity}
                      disabled={addActivityLoading}
                    >
                      {addActivityLoading ? (
                        <>
                          <Loader2 className="animate-spin h-4 w-4 mr-2" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          <span>Save</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="w-full md:w-1/2 flex items-end">
            <button
              type="button"
              className="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 flex items-center justify-center"
              onClick={handleAddCompartmentActivity}
              disabled={!selectedActivity || savingChanges}
            >
              {savingChanges ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  <span>Adding...</span>
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  <span>Add Activity to Compartment</span>
                </>
              )}
            </button>
          </div>
        </div>
        
        {hasChanges && (
          <div className="mb-6">
            <button
              type="button"
              className="w-full px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center justify-center"
              onClick={handleSaveSequenceChanges}
              disabled={savingChanges}
            >
              {savingChanges ? (
                <>
                  <Loader2 className="animate-spin h-5 w-5 mr-2" />
                  <span>Saving Changes...</span>
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  <span>Save Sequence Changes</span>
                </>
              )}
            </button>
          </div>
        )}
        
        {compartmentActivities.length === 0 ? (
          <div className="text-center p-8 bg-white rounded-lg shadow-sm border">
            <p className="text-gray-500">No activities mapped for this compartment</p>
          </div>
        ) : (
          <div className="relative mt-8">
            <div className="hidden md:block absolute h-full w-0.5 bg-gray-200 left-1/2 transform -translate-x-1/2"></div>
            
            {compartmentActivities.map((item, index) => {
              const isEven = index % 2 === 1;
              const boxColor = generateBoxColor(index);
              const dotColor = generateDotColor(index);
              
              return (
                <div
                  key={item.id || index}
                  className="relative mb-6"
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(index)}
                >
                  <div className={`flex flex-col md:flex-row ${isEven ? 'md:flex-row-reverse' : ''} items-center`}>
                    <div className={`flex w-full md:w-1/2 ${isEven ? 'md:justify-start md:pl-8' : 'md:justify-end md:pr-8'} mb-2 md:mb-0`}>
                      <div className={`rounded-lg ${boxColor} shadow-md border p-4 w-full md:max-w-xs transition-shadow duration-200 hover:shadow-lg cursor-grab`}>
                        <div className="flex items-center mb-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white mr-3 ${dotColor}`}>
                            {item.sequence}
                          </div>
                          <h3 className="font-semibold text-gray-900 flex-grow">{item.compartment_activity.name}</h3>
                          <button 
                    className="p-1 hover:bg-gray-200 rounded-md text-red-600 hover:text-red-800 ml-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedActivityToDelete(item.id);
                      setDeleteDialogVisible(true);
                    }}
                    disabled={deleteActivityLoading === item.id}
                  >
                    {deleteActivityLoading === item.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                          {getActivityIcon(item.compartment_activity.name)}
                        </div>
                        
                        <div className="text-sm">
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-500">Compartment:</span>
                            <span className="text-gray-700">{item.compartment?.name || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="hidden md:block w-1/2"></div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>Drag steps to reorder them</p>
        </div>
      </div>
    </div>
  );
};

export default CompartmentActivityMapping;